import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { chatAPI, reportsAPI, usersAPI, reviewsAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Send, ArrowLeft, AlertTriangle, Shield, Flag, Ban, Info, X, Star, BadgeCheck, ExternalLink, Bell, BellOff, Trash2, Phone } from 'lucide-react';
import { toast } from 'sonner';
import TrustedSellerBadge from '../components/TrustedSellerBadge';
import { showNotification, requestNotificationPermission, notificationsSupported, isAppleDevice, isStandaloneIOS, subscribeToVapidPush } from '../utils/notifications';
import api from '../utils/api';
import { isAndroidORIosApp } from 'webtonative';

const ChatPage = () => {
  const { conversationId } = useParams();
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [activeConvo, setActiveConvo] = useState(null);
  const [sending, setSending] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [notifPermission, setNotifPermission] = useState(typeof Notification !== 'undefined' ? Notification.permission : 'unsupported');
  // Presence: typing indicator + read receipts
  const [peerTypingAt, setPeerTypingAt] = useState(null);
  const [peerLastReadAt, setPeerLastReadAt] = useState(null);
  const [nowTick, setNowTick] = useState(Date.now()); // re-render every 2s so "typing…" fades on schedule
  const messagesEndRef = useRef(null);
  const outerRef = useRef(null); // iter64 — outer chat container ref for visualViewport anchoring
  const lastSeenMsgId = useRef(null);
  // Track latest seen message_id per conversation for cross-conversation notifications
  const lastSeenByConvo = useRef({});
  const lastTypingSentAt = useRef(0);
  const { user, loading: authLoading } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const pollingRef = useRef(null);
  const convoPollingRef = useRef(null);
  const statePollingRef = useRef(null);

  const loadConversations = useCallback(async () => {
    try {
      const res = await chatAPI.getConversations();
      const data = res.data || [];
      // Cross-conversation notification: detect new messages in ANY conversation since last poll
      if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        for (const c of data) {
          const lastMsg = c.last_message;
          if (!lastMsg || !lastMsg.message_id) continue;
          const prevSeen = lastSeenByConvo.current[c.conversation_id];
          const isMine = lastMsg.sender_id === user?.user_id;
          // Skip if message is mine, already seen, or this is the active conversation (handled by per-convo poll)
          if (!isMine && prevSeen && prevSeen !== lastMsg.message_id && c.conversation_id !== activeConvo) {
            // Use SW-aware helper so iOS PWA installs receive notifications
            showNotification(`GenzoX — ${c.other_user?.name || 'New message'}`, {
              body: (lastMsg.content || '').slice(0, 120) || 'You have a new message',
              icon: '/favicon-192.png',
              tag: `genzox-chat-${c.conversation_id}`,
              data: { url: `/chat/${c.conversation_id}` },
            });
          }
          lastSeenByConvo.current[c.conversation_id] = lastMsg.message_id;
        }
      } else {
        // Seed the map without notifying when permission not granted
        for (const c of data) {
          if (c.last_message?.message_id) lastSeenByConvo.current[c.conversation_id] = c.last_message.message_id;
        }
      }
      setConversations(data);
      // Auto-select first conversation only on desktop AND when nothing in URL
      if (!conversationId && data.length > 0 && window.innerWidth >= 768) {
        navigate(`/chat/${data[0].conversation_id}`, { replace: true });
      }
    } catch (e) { console.error('Failed to load conversations:', e); }
  }, [conversationId, navigate, user, activeConvo]);

  const loadMessages = useCallback(async (cid) => {
    try {
      const res = await chatAPI.getMessages(cid);
      const newMsgs = res.data;
      // Push notification for new incoming messages (not from me, not initial load)
      if (lastSeenMsgId.current && newMsgs.length > 0) {
        const lastIdx = newMsgs.findIndex(m => m.message_id === lastSeenMsgId.current);
        const unseen = lastIdx >= 0 ? newMsgs.slice(lastIdx + 1) : [];
        const incomingUnseen = unseen.filter(m => m.sender_id !== user?.user_id);
        if (incomingUnseen.length > 0 && typeof Notification !== 'undefined' && Notification.permission === 'granted' && document.visibilityState !== 'visible') {
          const msg = incomingUnseen[incomingUnseen.length - 1];
          showNotification('GenzoX — New message', {
            body: msg.content?.slice(0, 120) || 'You have a new message',
            icon: '/favicon-192.png',
            tag: `genzox-chat-${cid}`,
            data: { url: `/chat/${cid}` },
          });
        }
      }
      if (newMsgs.length > 0) {
        lastSeenMsgId.current = newMsgs[newMsgs.length - 1].message_id;
      }
      // Merge server messages with any local "sending"/"failed" pending ones so
      // the optimistic bubbles stay visible while the server catches up.
      setMessages((prev) => {
        const pending = prev.filter((m) => m.__pending && !newMsgs.some((sm) => sm.__client_id === m.__client_id));
        return [...newMsgs, ...pending];
      });
    } catch (e) {
      // ── DO NOT SILENTLY SWALLOW ── previously this catch was a no-op which
      // left iOS users staring at an empty conversation window with no
      // explanation. Surface a real error so we can debug in production.
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      console.error('[chat] loadMessages failed', { status, detail, err: e });
      if (status === 401) {
        toast.error('Session expired — please sign in again');
        navigate('/login');
      } else if (status === 403) {
        toast.error('You do not have access to this conversation.');
      } else if (status === 404) {
        toast.error('Conversation not found.');
      } else if (!e?.response) {
        // Network-level error (WebView offline, DNS fail, etc.). Do NOT clear
        // the existing message list — leave what's on screen so the user
        // still sees history.
        toast.error('Chat is offline — retrying…');
      } else {
        toast.error(detail || 'Failed to load messages');
      }
    }
  }, [user, navigate]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      // iter89 — Preserve the intended URL (conversation deep-link from a
      // push notification) so login bounces the user right back to the chat.
      const returnTo = `${window.location.pathname}${window.location.search}`;
      navigate(`/login?next=${encodeURIComponent(returnTo)}`);
      return;
    }
    loadConversations();
    // Auto-request notification permission once (only if user hasn't decided yet AND it's actually supported).
    // On iOS Safari (non-PWA) AND iOS Chrome (CriOS — no Notification API at all), notifications are NOT supported.
    // Iter51 fix: guard EVERY Notification access — accessing Notification.permission throws ReferenceError
    // on iPhone Chrome where the global doesn't exist, which previously crashed ChatPage on mount.
    const notifGlobalExists = typeof Notification !== 'undefined';
    if (notificationsSupported() && notifGlobalExists && Notification.permission === 'default') {
      // Defer slightly so it doesn't interrupt page paint
      setTimeout(() => {
        requestNotificationPermission().then((perm) => {
          setNotifPermission(perm);
          if (perm === 'granted') {
            // Iter50: also register for server-initiated VAPID push so notifications fire when app is closed
            subscribeToVapidPush(api).catch(() => {});
          }
        }).catch(() => {});
      }, 800);
    } else if (notifGlobalExists && Notification.permission === 'granted') {
      // Already granted — make sure the VAPID subscription is registered (idempotent upsert).
      subscribeToVapidPush(api).catch(() => {});
    }
    // Poll conversations list every 8s for cross-convo notifications + unread tracking
    if (convoPollingRef.current) clearInterval(convoPollingRef.current);
    convoPollingRef.current = setInterval(() => { loadConversations(); }, 8000);
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
      if (convoPollingRef.current) clearInterval(convoPollingRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading, navigate]);

  // If URL has a conversationId we don't yet have in the cached list, fetch it directly so
  // the chat header + send box render immediately (no "Select a conversation..." flash).
  useEffect(() => {
    if (!conversationId) return;
    const inList = conversations.some((c) => c.conversation_id === conversationId);
    if (inList) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await chatAPI.getConversation(conversationId);
        if (cancelled || !res?.data) return;
        setConversations((prev) => {
          if (prev.some((c) => c.conversation_id === res.data.conversation_id)) return prev;
          return [res.data, ...prev];
        });
      } catch (e) { /* 404 handled by activeConvoData fallback */ }
    })();
    return () => { cancelled = true; };
  }, [conversationId, conversations]);

  useEffect(() => {
    if (conversationId) {
      loadMessages(conversationId);
      setActiveConvo(conversationId);
      // Mark as read immediately on opening the convo
      chatAPI.markRead(conversationId).catch(() => {});
      // Reset peer state when switching threads
      setPeerTypingAt(null);
      setPeerLastReadAt(null);
      // Start polling for new messages
      if (pollingRef.current) clearInterval(pollingRef.current);
      pollingRef.current = setInterval(() => {
        loadMessages(conversationId);
        // Re-mark read while user is actively viewing the tab
        if (document.visibilityState === 'visible') {
          chatAPI.markRead(conversationId).catch(() => {});
        }
      }, 5000);
      // Poll presence (peer typing + last_read) at a faster cadence
      if (statePollingRef.current) clearInterval(statePollingRef.current);
      const pollState = async () => {
        try {
          const res = await chatAPI.getState(conversationId);
          setPeerTypingAt(res.data?.peer_typing_at || null);
          setPeerLastReadAt(res.data?.peer_last_read_at || null);
        } catch (_) {}
      };
      pollState();
      statePollingRef.current = setInterval(pollState, 3000);
    } else {
      setActiveConvo(null);
      setPeerTypingAt(null);
      setPeerLastReadAt(null);
      if (pollingRef.current) clearInterval(pollingRef.current);
      if (statePollingRef.current) clearInterval(statePollingRef.current);
    }
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
      if (statePollingRef.current) clearInterval(statePollingRef.current);
    };
  }, [conversationId, loadMessages]);

  // 2s tick so the "typing…" indicator and read-receipt staleness re-evaluate without
  // requiring a network round-trip on every render.
  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 2000);
    return () => clearInterval(id);
  }, []);

  // ── Foreground refresh (iter83) ────────────────────────────────────────
  // Fixes the reported iOS/Android bug where messages appeared stale after
  // the user backgrounded the app and came back. setInterval polling does
  // not fire immediately on foreground return — the next tick can be up to
  // 5s away — so we listen for the browser's visibility signal and force a
  // fresh fetch the instant the WebView becomes visible again. Also covers
  // the "pageshow" event which is what iOS Safari/WKWebView fires when the
  // page is restored from the back-forward cache.
  useEffect(() => {
    const forceRefresh = () => {
      if (document.visibilityState !== 'visible') return;
      // Refresh the conversation list (unread badges, last message previews)
      loadConversations();
      // Refresh the active chat if one is open
      if (activeConvo) {
        loadMessages(activeConvo);
        chatAPI.markRead(activeConvo).catch(() => {});
      }
    };
    document.addEventListener('visibilitychange', forceRefresh);
    window.addEventListener('pageshow', forceRefresh);
    window.addEventListener('focus', forceRefresh);
    return () => {
      document.removeEventListener('visibilitychange', forceRefresh);
      window.removeEventListener('pageshow', forceRefresh);
      window.removeEventListener('focus', forceRefresh);
    };
  }, [activeConvo, loadConversations, loadMessages]);

  // iter64 — PERMANENT WhatsApp-style mobile keyboard handling.
  //
  // BACKGROUND (true root cause finally found):
  // iter61–iter63 all kept failing on iPhone 16 Pro because iOS Safari/Chrome have a
  // documented quirk: they IGNORE `interactive-widget=resizes-content` and instead
  //   (a) shrink ONLY the visual viewport when the keyboard opens (layout viewport stays),
  //   (b) AUTO-SCROLL the layout viewport upward to bring the focused input into the
  //       visible band.
  // When the chat container was `position: fixed; top: 0`, that anchored it to the
  // document top — but the document top is now OFF-SCREEN (above the visible band) after
  // iOS scrolled. Result: chat appears to "shift upward", blank area, user must scroll.
  //
  // THE PERMANENT FIX (used by WhatsApp Web / Telegram Web / Signal Web):
  //  1. Anchor the chat container to `visualViewport.offsetTop` + `visualViewport.height`,
  //     i.e. follow the *visible* band, not the document. This is the critical piece
  //     missed by iter61–iter63.
  //  2. Listen to `visualViewport.resize`, `visualViewport.scroll`, AND `focusin/focusout`
  //     (iOS sometimes lags on vv.scroll, but focusin always fires). Force-update on each.
  //  3. Lock body with `position: fixed` (not just `overflow: hidden`) — iOS ignores
  //     overflow:hidden during keyboard interactions but respects position:fixed.
  //  4. On Android Chrome, `interactive-widget=resizes-content` (set in public/index.html)
  //     shrinks the layout viewport natively, so vv.offsetTop stays 0 and vv.height = window
  //     height. Same code path works — vv handlers just see height changes, no extra logic.
  useEffect(() => {
    const el = outerRef.current;
    if (!el) return;

    // 1. Bulletproof body scroll lock (position:fixed beats iOS keyboard auto-scroll)
    const prev = {
      bodyPosition: document.body.style.position,
      bodyOverflow: document.body.style.overflow,
      bodyWidth: document.body.style.width,
      bodyTop: document.body.style.top,
      bodyLeft: document.body.style.left,
      htmlOverflow: document.documentElement.style.overflow,
    };
    document.body.style.position = 'fixed';
    document.body.style.overflow = 'hidden';
    document.body.style.width = '100%';
    document.body.style.top = '0';
    document.body.style.left = '0';
    document.documentElement.style.overflow = 'hidden';

    // 2. Sync the chat container to the visible band on every viewport change
    const vv = (typeof window !== 'undefined') ? window.visualViewport : null;
    const update = () => {
      const top = vv ? vv.offsetTop : 0;
      const height = vv ? vv.height : window.innerHeight;
      el.style.top = `${top}px`;
      el.style.height = `${height}px`;
    };
    update();

    if (vv) {
      vv.addEventListener('resize', update);
      vv.addEventListener('scroll', update);
    }
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);

    // 3. iOS-specific: visualViewport.scroll can lag focus by ~50–300ms.
    // Force re-anchor on focusin/focusout AND a few delayed beats to catch iOS's
    // animated keyboard slide-in.
    const onFocusChange = () => {
      update();
      setTimeout(update, 50);
      setTimeout(update, 150);
      setTimeout(update, 350);
    };
    document.addEventListener('focusin', onFocusChange);
    document.addEventListener('focusout', onFocusChange);

    return () => {
      document.body.style.position = prev.bodyPosition;
      document.body.style.overflow = prev.bodyOverflow;
      document.body.style.width = prev.bodyWidth;
      document.body.style.top = prev.bodyTop;
      document.body.style.left = prev.bodyLeft;
      document.documentElement.style.overflow = prev.htmlOverflow;
      if (vv) {
        vv.removeEventListener('resize', update);
        vv.removeEventListener('scroll', update);
      }
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
      document.removeEventListener('focusin', onFocusChange);
      document.removeEventListener('focusout', onFocusChange);
    };
  }, []);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const enableNotifications = async () => {
    // Inside the WebToNative wrapper, web Notifications API is unavailable.
    // Native push is delivered by the OneSignal native SDK (see
    // OneSignalProvider) — permission is managed by the device's app
    // settings, not by a browser permission prompt. Show a clear, actionable
    // toast instead of the misleading "browser not supported" error.
    if (isAndroidORIosApp) {
      toast.info(
        'Notifications are managed in your phone\'s App Settings → Notifications → GenzoX.',
        { duration: 7000 }
      );
      return;
    }
    if (typeof Notification === 'undefined') {
      toast.error('Notifications not supported in this browser');
      return;
    }
    // iOS Safari requires the site to be added to Home Screen (PWA / standalone) before
    // notifications can be granted. Surface a clear instruction instead of silently failing.
    if (isAppleDevice() && !isStandaloneIOS()) {
      toast.info(
        'On iPhone/iPad: tap the Share button, then "Add to Home Screen" to install GenzoX. Open the app from your Home Screen, then tap this bell again to enable chat notifications.',
        { duration: 9000 }
      );
      return;
    }
    if (Notification.permission === 'granted') {
      toast.success('Notifications are already enabled');
      setNotifPermission('granted');
      return;
    }
    const perm = await requestNotificationPermission();
    setNotifPermission(perm);
    if (perm === 'granted') {
      toast.success('Chat notifications enabled');
      // Iter50: register for server-initiated VAPID push (works when app closed/background)
      subscribeToVapidPush(api).catch(() => {});
    }
    else toast.error('Notifications blocked — enable them in browser settings');
  };

  const openSellerProfile = async () => {
    const otherUser = activeConvoData?.other_user;
    if (!otherUser?.user_id) return;
    setShowProfile(true);
    setProfileLoading(true);
    try {
      const [userRes, reviewsRes] = await Promise.all([
        usersAPI.getPublic(otherUser.user_id),
        reviewsAPI.getSellerReviews(otherUser.user_id).catch(() => ({ data: { average_rating: 0, total_reviews: 0 } }))
      ]);
      setProfileData({ ...userRes.data, ...reviewsRes.data });
    } catch (e) {
      toast.error('Failed to load profile');
      setProfileData({ ...otherUser });
    } finally {
      setProfileLoading(false);
    }
  };

  const handleSend = async () => {
    if (!newMessage.trim() || sending) return;
    const convo = conversations.find(c => c.conversation_id === activeConvo);
    if (!convo) return;
    const receiverId = convo.buyer_id === user.user_id ? convo.seller_id : convo.buyer_id;
    const content = newMessage;
    // ── Optimistic render ── inject the message bubble immediately so slow
    // iOS/Android WebView networks don't make the app feel frozen. The bubble
    // is marked __pending until the server round-trip completes; on failure
    // the user can tap Retry rather than losing their draft.
    const clientId = `local_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const optimistic = {
      __pending: true,
      __client_id: clientId,
      message_id: clientId,
      conversation_id: activeConvo,
      sender_id: user.user_id,
      receiver_id: receiverId,
      content,
      message_type: 'text',
      created_at: new Date().toISOString(),
      is_read: false,
    };
    setMessages((prev) => [...prev, optimistic]);
    setNewMessage('');
    setSending(true);
    try {
      const res = await chatAPI.sendMessage({
        listing_id: convo.listing_id,
        receiver_id: receiverId,
        content,
        message_type: 'text',
      });
      // Backend returns the persisted message — swap the optimistic bubble
      // for the server one (matched via __client_id we stamp on the pending row).
      setMessages((prev) => {
        const stamped = { ...(res?.data || {}), __client_id: clientId };
        return prev.map((m) => (m.__client_id === clientId ? stamped : m));
      });
      // Clear our own typing flag immediately so peer doesn't see stale "typing…"
      lastTypingSentAt.current = 0;
      // Kick a background refresh to sync anything else that changed
      loadMessages(activeConvo);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      console.error('[chat] sendMessage failed', { status, detail, err: e });
      // Mark the optimistic bubble as failed so the user sees "Not sent"
      // and can retry / edit.
      setMessages((prev) => prev.map((m) => (m.__client_id === clientId ? { ...m, __pending: false, __failed: true } : m)));
      if (status === 401) {
        toast.error('Session expired — please sign in again');
        navigate('/login');
      } else if (status === 403) {
        toast.error(detail || 'You are blocked from messaging this user.');
      } else if (status === 429) {
        toast.error('Slow down — you are sending messages too fast.');
      } else if (!e?.response) {
        toast.error('No network — message will be lost. Please retry.');
      } else {
        toast.error(detail || 'Failed to send message');
      }
      // Restore the draft so the user can retry without retyping.
      setNewMessage(content);
    } finally {
      setSending(false);
    }
  };

  // Throttled typing signal — sent at most once every 3s while the user keeps typing.
  const handleInputChange = (e) => {
    setNewMessage(e.target.value);
    if (!activeConvo) return;
    const now = Date.now();
    if (now - lastTypingSentAt.current > 3000) {
      lastTypingSentAt.current = now;
      chatAPI.markTyping(activeConvo).catch(() => {});
    }
  };

  // Peer is "typing" if their last typing-at is within the last 5 seconds.
  const isPeerTyping = (() => {
    if (!peerTypingAt) return false;
    const ts = Date.parse(peerTypingAt);
    if (!ts) return false;
    return nowTick - ts < 5000;
  })();

  // Find my last sent message (chronologically) to attach the read receipt to.
  const myLastMsgId = (() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].sender_id === user?.user_id) return messages[i].message_id;
    }
    return null;
  })();

  const isMessageRead = (msg) => {
    if (!peerLastReadAt) return false;
    const sentTs = Date.parse(msg.created_at);
    const readTs = Date.parse(peerLastReadAt);
    return sentTs && readTs && sentTs <= readTs;
  };

  const handleBlockUser = async (userId) => {
    try {
      await reportsAPI.blockUser(userId);
      toast.success('User blocked');
    } catch (e) { toast.error('Failed to block user'); }
  };

  const handleReportUser = async (userId) => {
    try {
      await reportsAPI.create({ reported_user_id: userId, reason: 'suspicious_chat' });
      toast.success('User reported');
    } catch (e) { toast.error('Failed to report user'); }
  };

  // iter58 — Share my contact number into the chat (user-initiated only).
  // Opens a confirmation, then sends a message with message_type='contact' so the
  // receiver sees a formatted card. Phone number is taken from the logged-in user's
  // profile — we never auto-reveal numbers.
  const handleShareContact = async () => {
    if (!activeConvo) return;
    const convo = conversations.find(c => c.conversation_id === activeConvo);
    if (!convo) return;
    const myPhone = (user?.phone || '').trim();
    if (!myPhone) {
      toast.error('No phone number on your profile. Add one in your profile to share.');
      return;
    }
    if (!window.confirm(`Share your phone number (${myPhone}) with ${convo.other_user?.name || 'this user'}?`)) return;
    const receiverId = convo.buyer_id === user.user_id ? convo.seller_id : convo.buyer_id;
    setSending(true);
    try {
      await chatAPI.sendMessage({
        listing_id: convo.listing_id,
        receiver_id: receiverId,
        content: myPhone,
        message_type: 'contact',
      });
      loadMessages(activeConvo);
      toast.success('Your contact number was shared');
    } catch (e) {
      toast.error('Failed to share contact');
    } finally {
      setSending(false);
    }
  };

  // iter58 — Soft-delete a conversation from the current user's view.
  // Confirmation prompt required. Backend retains the record for moderation /
  // dispute resolution; if the other user sends a fresh message, the conv reappears.
  const handleDeleteConversation = async (convId, otherName, e) => {
    if (e) { e.stopPropagation(); e.preventDefault(); }
    if (!convId) return;
    const peer = otherName || 'this user';
    if (!window.confirm(`Delete your chat with ${peer}?\n\nThis only hides the conversation from your list. Messages are retained for dispute resolution and will reappear if you receive a new message from this user.`)) return;
    try {
      await chatAPI.deleteConversation(convId);
      setConversations((prev) => prev.filter((c) => c.conversation_id !== convId));
      if (activeConvo === convId) {
        setActiveConvo(null);
        navigate('/chat');
      }
      toast.success('Conversation deleted');
    } catch (_err) {
      toast.error('Failed to delete conversation');
    }
  };

  const activeConvoData = conversations.find(c => c.conversation_id === activeConvo);

  return (
    // iter64 — Outer chat container is anchored to the *visual viewport* via JS (see useEffect
    // above). `left/right: 0` + dynamic inline `top`/`height` mean it always matches the
    // visible band exactly — including when iOS scrolls the layout viewport to reveal a
    // focused input. Tailwind sets only fixed positioning + clipping; positioning math is JS.
    <div
      ref={outerRef}
      data-testid="chat-outer"
      className="fixed left-0 right-0 bg-[#F8F9FA] overflow-hidden flex flex-col"
      style={{ top: 0, height: '100dvh' }}
    >
      <div className="max-w-7xl mx-auto w-full flex-1 min-h-0">
        <div className="flex h-full">
          {/* Conversations List - hidden on mobile when chat is active */}
          <div className={`${activeConvo ? 'hidden md:flex' : 'flex'} w-full md:w-80 bg-white border-r border-gray-100 flex-col min-h-0`} data-testid="conversations-list">
            <div className="p-3 sm:p-4 border-b border-[#1800ad]/10 bg-[#f0edff]">
              <Link to="/" className="text-[#1800ad] hover:text-[#ff8500] text-xs font-medium mb-1 inline-block">&larr; Back to Home</Link>
              <h2 className="text-base sm:text-lg font-bold text-[#1800ad]">{t('messages')}</h2>
            </div>
            <div className="flex-1 overflow-y-auto">
              {conversations.length === 0 ? (
                <div className="p-6 text-center text-sm text-[#9CA3AF]">No conversations yet</div>
              ) : conversations.map((convo) => {
                const isUnread = (convo.unread_count || 0) > 0;
                const lastPreview = convo.last_message?.message_type === 'contact'
                  ? `📞 ${convo.last_message?.content || 'Contact shared'}`
                  : (convo.last_message?.content || convo.listing?.title || 'Listing');
                return (
                <div key={convo.conversation_id} className="relative group">
                  <button data-testid={`convo-${convo.conversation_id}`}
                    onClick={() => navigate(`/chat/${convo.conversation_id}`)}
                    className={`w-full p-3 sm:p-4 text-left border-b border-gray-50 hover:bg-[#f0edff] transition-colors ${activeConvo === convo.conversation_id ? 'bg-[#f0edff] border-l-4 border-l-[#1800ad]' : ''}`}>
                    <div className="flex items-center gap-3">
                      {convo.other_user?.picture ? (
                        <img src={convo.other_user.picture} alt="" className="w-10 h-10 rounded-full" referrerPolicy="no-referrer" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-[#1800ad] flex items-center justify-center text-white font-bold text-sm">
                          {convo.other_user?.name?.[0] || '?'}
                        </div>
                      )}
                      <div className="flex-1 min-w-0 pr-7">
                        <div className="flex items-center gap-1.5">
                          <p className={`text-sm text-[#0A0B10] truncate ${isUnread ? 'font-extrabold' : 'font-bold'}`}>{convo.other_user?.name || 'User'}</p>
                          {isUnread && (
                            <span data-testid={`convo-unread-dot-${convo.conversation_id}`} className="inline-flex min-w-[18px] h-[18px] px-1 items-center justify-center rounded-full bg-[#ff8500] text-white text-[10px] font-black">
                              {convo.unread_count > 9 ? '9+' : convo.unread_count}
                            </span>
                          )}
                        </div>
                        <p data-testid={`convo-preview-${convo.conversation_id}`} className={`text-xs truncate ${isUnread ? 'text-[#0A0B10] font-bold' : 'text-[#6B7280]'}`}>{lastPreview}</p>
                      </div>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={(e) => handleDeleteConversation(convo.conversation_id, convo.other_user?.name, e)}
                    data-testid={`convo-delete-${convo.conversation_id}`}
                    title="Delete conversation"
                    className="absolute top-2 right-2 p-1.5 rounded-full bg-white/0 text-[#9CA3AF] hover:bg-red-50 hover:text-red-600 transition-colors opacity-100 md:opacity-0 md:group-hover:opacity-100 md:focus:opacity-100"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
                );
              })}
            </div>
          </div>

          {/* Chat Area - full screen on mobile */}
          <div className={`${activeConvo ? 'flex' : 'hidden md:flex'} flex-1 flex-col bg-[#f5f3ff] min-h-0`} data-testid="chat-area">
            {activeConvo ? (
              <>
                {/* Chat Header — iter61: flex-shrink-0 ensures it never collapses
                    or scrolls out of view even on very long conversations. */}
                <div className="flex-shrink-0 px-3 sm:px-6 py-3 sm:py-4 bg-[#1800ad] flex items-center justify-between">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <button onClick={() => navigate('/chat')} className="md:hidden p-1.5 rounded-lg bg-white/10 text-white hover:bg-white/20" data-testid="chat-back-btn">
                      <ArrowLeft size={20} />
                    </button>
                    <button
                      onClick={openSellerProfile}
                      data-testid="chat-header-profile-btn"
                      className="flex items-center gap-2 sm:gap-3 hover:bg-white/10 -ml-1 pl-1 pr-2 py-1 rounded-lg transition-colors text-left"
                      title="View profile"
                    >
                      {activeConvoData?.other_user?.picture ? (
                        <img src={activeConvoData.other_user.picture} alt="" className="w-9 h-9 rounded-full border-2 border-white/30" referrerPolicy="no-referrer" />
                      ) : (
                        <div className="w-9 h-9 rounded-full bg-[#ff8500] flex items-center justify-center text-white font-bold text-xs">
                          {activeConvoData?.other_user?.name?.[0] || '?'}
                        </div>
                      )}
                      <div>
                        <p className="font-bold text-sm text-white flex items-center gap-1">
                          {activeConvoData?.other_user?.name || 'Loading…'}
                          {activeConvoData?.other_user?.aadhaar_verified && (
                            <BadgeCheck size={13} className="text-[#ff8500]" />
                          )}
                        </p>
                        <p className="text-xs text-white/70">{activeConvoData?.listing?.title || ''}</p>
                      </div>
                    </button>
                  </div>
                  <div className="flex gap-2">
                    <button data-testid="enable-notifications-btn" onClick={enableNotifications}
                      className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white/80 hover:text-white transition-colors" title={notifPermission === 'granted' ? 'Notifications on' : 'Enable notifications'}>
                      {notifPermission === 'granted' ? <Bell size={16} /> : <BellOff size={16} />}
                    </button>
                    <button data-testid="report-user-btn" onClick={() => handleReportUser(activeConvoData?.other_user?.user_id)}
                      className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white/80 hover:text-white transition-colors" title={t('report_user')}>
                      <Flag size={16} />
                    </button>
                    <button data-testid="block-user-btn" onClick={() => handleBlockUser(activeConvoData?.other_user?.user_id)}
                      className="p-2 rounded-lg bg-white/10 hover:bg-red-500/80 text-white/80 hover:text-white transition-colors" title={t('block_user')}>
                      <Ban size={16} />
                    </button>
                    <button data-testid="delete-conversation-btn"
                      onClick={() => handleDeleteConversation(activeConvo, activeConvoData?.other_user?.name)}
                      className="p-2 rounded-lg bg-white/10 hover:bg-red-500/80 text-white/80 hover:text-white transition-colors" title="Delete conversation">
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>

                {/* Safety Banner — iter61: flex-shrink-0 keeps it pinned beneath the header */}
                <div className="flex-shrink-0 px-6 py-2.5 bg-[#fff4ed] border-b border-[#ff8500]/20 flex items-center gap-2">
                  <Shield size={14} className="text-[#ff8500]" />
                  <p className="text-[11px] text-[#9A4500] font-medium">{t('never_share')}</p>
                </div>

                {/* iter61 — Contact share CTA, positioned ABOVE the conversation area.
                    More discoverable than the previous icon-only button next to the input.
                    Uses GenzoX brand green (#00bf63). Action remains user-initiated. */}
                <div className="flex-shrink-0 px-3 sm:px-6 py-2 bg-white border-b border-gray-100">
                  <button
                    type="button"
                    data-testid="share-contact-btn"
                    onClick={handleShareContact}
                    disabled={sending}
                    className="w-full flex items-center justify-center gap-2 bg-[#00bf63] hover:bg-[#00a957] text-white rounded-xl px-3 py-2 sm:py-2.5 text-xs sm:text-sm font-semibold transition-colors disabled:opacity-50 shadow-sm"
                  >
                    <Phone size={14} />
                    <span>Share your contact number with the {activeConvoData?.buyer_id === user?.user_id ? 'seller' : 'buyer'}</span>
                  </button>
                </div>

                {/* Messages */}
                <div className="flex-1 min-h-0 overflow-y-auto px-3 sm:px-6 py-4 space-y-3" data-testid="messages-area">
                  {messages.map((msg) => {
                    const isMine = msg.sender_id === user.user_id;
                    const isOffer = msg.message_type === 'offer';
                    const isContact = msg.message_type === 'contact';
                    return (
                      <div key={msg.message_id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                        <div className="max-w-[85%] sm:max-w-[70%]">
                          {msg.is_suspicious && (
                            <div className="flex items-center gap-1.5 mb-1 px-2">
                              <AlertTriangle size={11} className="text-amber-600" />
                              <span className="text-[10px] text-amber-700 font-medium">{t('suspicious_alert')}</span>
                            </div>
                          )}
                          {isOffer ? (
                            <div className={`px-4 py-3 rounded-2xl text-sm border-2 border-[#ff8500] bg-[#fff4ed] ${isMine ? 'rounded-br-md' : 'rounded-bl-md'}`}>
                              <div className="flex items-center gap-1.5 mb-1">
                                <span className="text-[10px] font-bold text-[#ff8500] uppercase tracking-wide">Offer</span>
                              </div>
                              <p className="text-[#0A0B10] font-medium">{msg.content}</p>
                            </div>
                          ) : isContact ? (
                            <div data-testid={`contact-card-${msg.message_id}`} className={`px-4 py-3 rounded-2xl text-sm border-2 border-[#1800ad] bg-[#f0edff] ${isMine ? 'rounded-br-md' : 'rounded-bl-md'}`}>
                              <div className="flex items-center gap-1.5 mb-1">
                                <Phone size={12} className="text-[#1800ad]" />
                                <span className="text-[10px] font-bold text-[#1800ad] uppercase tracking-wide">Contact shared</span>
                              </div>
                              <a href={`tel:${msg.content}`} className="text-[#0A0B10] font-bold underline decoration-[#1800ad]/40 hover:decoration-[#1800ad]">{msg.content}</a>
                            </div>
                          ) : (
                            <div className={`px-4 py-2.5 rounded-2xl text-sm ${isMine
                              ? 'bg-[#1800ad] text-white rounded-br-md'
                              : 'bg-white text-[#0A0B10] border border-gray-200 rounded-bl-md shadow-sm'
                            }`}>
                              {msg.content}
                            </div>
                          )}
                          <p className={`text-[10px] text-[#6B7280] mt-0.5 px-1 flex items-center gap-1 ${isMine ? 'justify-end' : ''}`}>
                            <span>{new Date(msg.created_at).toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit' })}</span>
                            {isMine && msg.__pending && (
                              <span data-testid="msg-sending-indicator" className="text-[#9CA3AF] italic">Sending…</span>
                            )}
                            {isMine && msg.__failed && (
                              <button
                                type="button"
                                data-testid={`msg-retry-${msg.__client_id}`}
                                onClick={() => { setNewMessage(msg.content); setMessages((prev) => prev.filter((m) => m.__client_id !== msg.__client_id)); }}
                                className="text-red-600 font-semibold underline"
                              >Not sent — tap to retry</button>
                            )}
                            {isMine && !msg.__pending && !msg.__failed && msg.message_id === myLastMsgId && (
                              isMessageRead(msg) ? (
                                <span data-testid="msg-read-indicator" className="text-[#1800ad] font-semibold">✓✓ Read</span>
                              ) : (
                                <span data-testid="msg-sent-indicator" className="text-[#9CA3AF]">✓ Sent</span>
                              )
                            )}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>

                {/* Input */}
                <div className="px-3 sm:px-6 py-3 sm:py-4 bg-white border-t border-gray-200 flex-shrink-0" style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}>
                  {isPeerTyping && (
                    <div data-testid="peer-typing-indicator" className="mb-2 inline-flex items-center gap-1.5 text-[11px] text-[#1800ad] font-semibold">
                      <span className="flex gap-0.5">
                        <span className="w-1 h-1 rounded-full bg-[#1800ad] animate-bounce" style={{ animationDelay: '0ms' }} />
                        <span className="w-1 h-1 rounded-full bg-[#1800ad] animate-bounce" style={{ animationDelay: '150ms' }} />
                        <span className="w-1 h-1 rounded-full bg-[#1800ad] animate-bounce" style={{ animationDelay: '300ms' }} />
                      </span>
                      <span>{activeConvoData?.other_user?.name?.split(' ')[0] || 'Seller'} is typing…</span>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <input data-testid="message-input" type="text" value={newMessage}
                      onChange={handleInputChange}
                      onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                      placeholder={t('type_message')}
                      className="flex-1 rounded-xl border border-gray-200 bg-gray-50 px-3 sm:px-4 py-2 sm:py-2.5 text-sm text-[#0A0B10] focus:border-[#1800ad] focus:ring-2 focus:ring-[#1800ad]/10 outline-none transition-all" />
                    <button data-testid="send-btn" onClick={handleSend} disabled={sending || !newMessage.trim()}
                      className="bg-[#ff8500] text-white rounded-xl px-4 sm:px-5 py-2 sm:py-2.5 hover:bg-[#e57500] transition-colors disabled:opacity-50">
                      <Send size={18} />
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <Info size={40} className="mx-auto mb-3 text-[#1800ad] opacity-40" />
                  <p className="text-sm text-[#6B7280]">Select a conversation to start chatting</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Seller Profile Modal */}
      {showProfile && (
        <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={() => setShowProfile(false)} data-testid="seller-profile-modal">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="relative bg-gradient-to-br from-[#1800ad] to-[#ff8500] p-6 rounded-t-2xl">
              <button onClick={() => setShowProfile(false)} className="absolute top-3 right-3 p-1.5 rounded-full bg-white/20 hover:bg-white/30 text-white" data-testid="close-profile-modal">
                <X size={18} />
              </button>
              {profileLoading ? (
                <div className="flex flex-col items-center text-white">
                  <div className="w-20 h-20 rounded-full bg-white/20 animate-pulse" />
                  <div className="h-4 w-32 bg-white/20 rounded animate-pulse mt-3" />
                </div>
              ) : (
                <div className="flex flex-col items-center text-white">
                  {profileData?.picture ? (
                    <img src={profileData.picture} alt={profileData.name} className="w-20 h-20 rounded-full border-4 border-white/40 object-cover" referrerPolicy="no-referrer" />
                  ) : (
                    <div className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center text-3xl font-bold">
                      {profileData?.name?.[0] || '?'}
                    </div>
                  )}
                  <h3 className="mt-3 text-lg font-bold flex items-center gap-1.5" data-testid="profile-name">
                    {profileData?.name || 'User'}
                    {profileData?.aadhaar_verified && <BadgeCheck size={18} className="text-white" />}
                  </h3>
                  {profileData?.total_reviews > 0 && (
                    <div className="flex items-center gap-1 mt-1 text-sm">
                      <Star size={14} className="fill-yellow-300 text-yellow-300" />
                      <span className="font-semibold">{profileData.average_rating}</span>
                      <span className="text-white/70">({profileData.total_reviews} reviews)</span>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="p-5 space-y-3">
              {profileData?.is_trusted_seller && !profileData?.is_dealer && (
                <div className="flex items-center justify-center pt-1">
                  <TrustedSellerBadge size="md" testId="chat-profile-trusted-badge" />
                </div>
              )}
              {profileData?.aadhaar_verified && (
                <div className="flex items-center gap-2 px-3 py-2 bg-[#f0edff] rounded-xl">
                  <BadgeCheck size={16} className="text-[#1800ad]" />
                  <span className="text-xs font-semibold text-[#1800ad]">Identity Verified</span>
                </div>
              )}
              {profileData?.phone_verified && (
                <div className="flex items-center gap-2 px-3 py-2 bg-[#fff4ed] rounded-xl">
                  <Shield size={16} className="text-[#ff8500]" />
                  <span className="text-xs font-semibold text-[#ff8500]">Phone Verified</span>
                </div>
              )}
              {profileData?.bio && (
                <p className="text-sm text-[#4B5563] leading-relaxed px-1">{profileData.bio}</p>
              )}
              <Link
                to={`/seller/${activeConvoData?.other_user?.user_id}`}
                onClick={() => setShowProfile(false)}
                data-testid="view-seller-listings-btn"
                className="flex items-center justify-center gap-2 w-full bg-[#1800ad] hover:bg-[#1200a0] text-white rounded-xl py-3 font-bold text-sm transition-colors"
              >
                <ExternalLink size={16} /> View Seller's Listings
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatPage;
